/**
 * OfflineStatusBar
 * Persistent banner showing connectivity status, pending sync count, and sync controls.
 * Integrates into the main Layout.
 */

import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Chip, LinearProgress } from '@mui/material';
import { WifiOff, Wifi, Sync, CloudDone, CloudOff } from '@mui/icons-material';
import offlineSyncService from '../../services/offlineSyncService';
import { getPendingSessions } from '../../services/offlineVerificationSession';

const OfflineStatusBar = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pendingCount, setPendingCount] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Check pending sessions periodically
  useEffect(() => {
    const check = async () => {
      try {
        const sessions = await getPendingSessions();
        setPendingCount(sessions.length);
      } catch {}
    };
    check();
    const interval = setInterval(check, 30000);
    return () => clearInterval(interval);
  }, []);

  // Listen to sync events
  useEffect(() => {
    const unsub = offlineSyncService.onStatusChange((event) => {
      if (event.type === 'sync_started') setSyncing(true);
      if (event.type === 'sync_completed') {
        setSyncing(false);
        setSyncResult(event.results);
        // Re-check pending count
        getPendingSessions().then(s => setPendingCount(s.length)).catch(() => {});
        // Clear result after 5s
        setTimeout(() => setSyncResult(null), 5000);
      }
      if (event.type === 'session_sync_failed') setSyncing(false);
    });
    return unsub;
  }, []);

  const handleSync = async () => {
    setSyncing(true);
    try {
      await offlineSyncService.syncAllPending();
    } catch (err) {
      console.error('Manual sync failed:', err);
      setSyncing(false);
    }
  };

  // Don't show if online with no pending items and no recent result
  if (isOnline && pendingCount === 0 && !syncResult) return null;

  return (
    <Box
      sx={{
        px: 2,
        py: 1,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        bgcolor: isOnline ? (pendingCount > 0 ? 'info.50' : 'success.50') : 'warning.50',
        borderBottom: '1px solid',
        borderColor: isOnline ? (pendingCount > 0 ? 'info.200' : 'success.200') : 'warning.200',
        flexWrap: 'wrap'
      }}
    >
      {/* Connection status */}
      {isOnline ? (
        <Chip icon={<Wifi />} label="Online" size="small" color="success" variant="outlined" />
      ) : (
        <Chip icon={<WifiOff />} label="Offline" size="small" color="warning" variant="outlined" />
      )}

      {/* Pending count */}
      {pendingCount > 0 && (
        <Typography variant="body2" sx={{ flex: 1 }}>
          {pendingCount} verification{pendingCount !== 1 ? 's' : ''} waiting to sync
        </Typography>
      )}

      {/* Sync progress */}
      {syncing && (
        <Box sx={{ flex: 1, minWidth: 100 }}>
          <LinearProgress sx={{ borderRadius: 2 }} />
        </Box>
      )}

      {/* Sync result */}
      {syncResult && !syncing && (
        <Chip
          icon={<CloudDone />}
          label={`Synced ${syncResult.filter(r => r.success).length}/${syncResult.length}`}
          size="small"
          color="success"
          variant="outlined"
        />
      )}

      {/* Sync button */}
      {isOnline && pendingCount > 0 && !syncing && (
        <Button
          size="small"
          variant="contained"
          startIcon={<Sync />}
          onClick={handleSync}
          sx={{ whiteSpace: 'nowrap' }}
        >
          Sync Now
        </Button>
      )}

      {/* Offline message */}
      {!isOnline && pendingCount > 0 && (
        <Typography variant="caption" color="text.secondary">
          Will sync automatically when online
        </Typography>
      )}
    </Box>
  );
};

export default OfflineStatusBar;
